import allSvgSrcs from "@theme/.generated/svg-urls";
import invariant from "invariant";

import { getProductBadgeVariant } from "@theme/components";

import { baseUrl } from "@theme/utils";

import { strapiLanguagesArray } from "@ms/gatsby-site/src/config/global";
import { MEASUREMENT_UNIT } from "@ms/gatsby-site/src/config/product-info";

import type {
  BoxerStrapiMeasurementType,
  TshirtStrapiMeasurementType,
} from "@ms/gatsby-site/src/types/product";
import type {
  LocaleHeadlineGroupProps,
  StrapiBlogAsset,
  StrapiHeadlineProps,
  StrapiImageInterface,
  StrapiLocaleAttributes,
  StrapiLocaleHeadlineProps,
} from "@ms/gatsby-site/src/types/strapi";
import type { StrapiVideoInterface } from "@theme/types";
import type {
  NavCategory,
  NavCategoryColumn,
  NavProduct,
  NavQueryCategory,
  NavQueryCategoryColumn,
  NavQueryProduct,
  NavSubCategory,
  NavigationStaticText,
  SiteNavData,
  SiteNavQueryData,
  StrapiSlugField,
} from "@theme/types";

/**
 * Strapi Utils - These utils are used to drill into and manipulate data received from Strapi, particularly for localizing content.
 * These utils live in theme as strapi is used in both gatsby-site and gatsby-blog.
 */

/**
 * Get Strapi Locale Content
 *
 * This function can be used for any module being pulled from strapi to ensure the correct locale is being used.
 *
 * It takes in the current language being used on the site as well as all localizations of the module from strapi
 *
 * Finds the localization to be used and returns it
 *
 * @param {string} language
 * @returns {{ locale: string, [key: string]: any }}
 */
export const getStrapiLocaleContent = <T>(
  language: string,
  localizations: StrapiLocaleAttributes<T>[] | null | undefined,
) =>
  Array.isArray(localizations)
    ? localizations.find(
        (item) => strapiLanguagesArray.includes(item.locale) && item.locale.includes(language),
      )
    : undefined;

/**
 * Get Strapi Locale Props
 *
 * This function can be used for any module that is completely localized.
 *
 * It takes in the current locale being used on the site, the english props, and the locale props.
 *
 * The intention here is to return the english props when in locale "en" (US english)
 *
 * Otherwise we use the original props overwritten with localized props. (i.e. {...props, ...localeContent})
 *
 * The locale props needs to be formatted on a case by case basis, as images, relations, and some props have distinct data structures in locale content.
 *
 * @param {string} currentLocale
 * @param {props: T} localizations
 * @returns {localeProps: T}
 */
export const getStrapiLocaleProps = <T>(
  currentLocale: string,
  props: T,
  localeProps?: T | null,
) => {
  if (currentLocale === "en") return props;

  return localeProps ? localeProps : null;
};

/**
 * Strapi Image Resolver
 *
 * Resolve strapi image queries to ensure image data is there
 *
 * @param image
 * @returns
 */
export const strapiImageResolver = (image: StrapiImageInterface) => {
  invariant(image?.cloudinaryMedia?.gatsbyImageData, "strapi image gatsbyImageData is required");

  return {
    childImageSharp: {
      gatsbyImageData: image.cloudinaryMedia.gatsbyImageData,
    },
  };
};

/**
 *
 * Check if rich text is empty
 *
 * We want to enforce types returned from strapi to be null if they do not exist - rich text fields, even if not required,
 * by default return an empty string  - we want to return null in those instances for proper type checking
 *
 * @param text
 * @returns {string | null}
 */
export const checkIfRichTextIsEmpty = (text: string) => (text === "" ? null : text);

/**
 * Flattens a Strapi slug field to a simple object with a slug property.
 *
 * This function is useful when working with Strapi responses where the slug field
 * may be directly on the object or nested under a `data.attributes` structure.
 *
 * @param {any} field - The Strapi slug field, which may be an object with `slug` property,
 *                      or have `data.attributes.slug` structure.
 * @returns {{ slug: string } | null} An object containing the slug if found, otherwise null.
 */
export const flattenStrapiSlugField = (field: StrapiSlugField) => {
  if (field && "slug" in field && typeof field.slug === "string") return { slug: field.slug };
  if (field && "data" in field && field.data?.attributes?.slug)
    return { slug: field.data.attributes.slug };

  return null;
};

/**
 * Merge content with locale content
 *
 * This function merges base content with localized content, with locale content taking precedence.
 * It handles deep merging of nested objects, allowing locale content to override specific fields
 * within nested objects (e.g., badge.label, button.label) while preserving other properties.
 *
 * @param {T} content - The base content object
 * @param {Record<string, any> | null | undefined} localeContent - The localized content to merge (can have partial nested objects)
 * @param {string[]} nestedFields - Optional array of field names that should be deep merged
 * @returns {T} - The merged content with locale content taking precedence
 *
 * @example
 * const base = { headline: "Hello", badge: { label: "New", theme: "gold" } };
 * const locale = { headline: "Bonjour", badge: { label: "Nouveau" } };
 * mergeWithLocale(base, locale, ["badge", "button"]);
 * // Returns: { headline: "Bonjour", badge: { label: "Nouveau", theme: "gold" } }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeWithLocale = <T extends Record<string, any>>(
  content: T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  localeContent?: Record<string, any> | null,
  nestedFields: string[] = [],
): T => {
  if (!localeContent) return content;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const merged: any = { ...content };

  for (const key in localeContent) {
    const localeValue = localeContent[key];
    const baseValue = content[key];

    if (
      nestedFields.includes(key) &&
      localeValue &&
      baseValue &&
      typeof baseValue === "object" &&
      typeof localeValue === "object" &&
      !Array.isArray(baseValue) &&
      !Array.isArray(localeValue)
    ) {
      merged[key] = {
        ...baseValue,
        ...localeValue,
      };
    } else if (localeValue !== undefined && localeValue !== null) {
      merged[key] = localeValue;
    }
  }

  return merged as T;
};

/**
 * Get Strapi Locale Headline Group
 *
 * Use this function to quickly localize all fields for headline groups when ingesting the strapi component.
 *
 */
export const getStrapiLocaleHeadlineGroup = (
  headlineGroup: StrapiHeadlineProps,
  localeHeadlineGroup?: StrapiLocaleHeadlineProps | null,
): LocaleHeadlineGroupProps => ({
  ...headlineGroup,
  eyebrow: localeHeadlineGroup?.eyebrow ?? headlineGroup.eyebrow,
  headline: localeHeadlineGroup?.headline ?? headlineGroup.headline,
  subtitle: localeHeadlineGroup?.subtitle ?? headlineGroup.subtitle,
  body: localeHeadlineGroup?.body ?? headlineGroup.body?.data.body,
  disclaimer: localeHeadlineGroup?.disclaimer ?? headlineGroup.disclaimer,
  buttons: headlineGroup.buttons
    ? headlineGroup.buttons.map((button, index) => ({
        ...button,
        label: localeHeadlineGroup?.buttons?.[index]?.label ?? button.label,
        ariaLabel: localeHeadlineGroup?.buttons?.[index]?.ariaLabel ?? button.ariaLabel,
      }))
    : null,
});

export const getStrapiLocaleVideoTranscript = (
  video: StrapiVideoInterface | null,
  language: string,
) => {
  const defaultTranscript = video?.transcript?.data?.transcript ?? null;
  const localeTranscript = video?.localizations
    ? getStrapiLocaleContent(language, video.localizations)
    : null;

  return localeTranscript?.transcript ?? defaultTranscript;
};

type SvgKey = keyof typeof allSvgSrcs;
/**
 * Resolve a Strapi-provided SVG key to a public URL generated by the theme build.
 *
 * The key must match the named export identifiers used in index files under
 * the `src/assets/svg/` directory tree (e.g., any nested `index.ts`).
 * For example:
 *   export { default as manscapedLogo } from "./manscaped-logo.svg";
 * The CMS should store "manscapedLogo" as the key.
 *
 * @param key - The Strapi key to look up.
 * @returns The public URL string for the SVG, or the fallback/undefined.
 *
 * @example
 * ```ts
 * import { getSvgFromStrapi } from "@theme/utils/getSvgFromStrapi";
 * const url = getSvgFromStrapi("manscapedLogo", { fallback: "/svg/default.svg" });
 * // use: <img src={url} alt="" />
 * ```
 */
const isSvgKey = (key: string): key is SvgKey => key in allSvgSrcs;

export const getSvgFromStrapi = (key?: string | null): string | null => {
  if (!key || !isSvgKey(key)) return null;

  return allSvgSrcs[key];
};

/**
 * Get Localized Nav Data
 *
 * This function massages the data received from strapi in our useNavData & useBlogNavData hooks.
 * It lives here so that we can use the same function for both the general sites and the blog, rather than
 * duplicating code between the two hooks. Also deals with handling inactive products and other exceptions.
 *
 * @param {SiteNavQueryData} siteNavData
 * @param {string} language
 * @param {string} currentMsMarket
 * @param {boolean} isBlog
 * @param {NavigationStaticText} navigationStaticText
 * @returns {SiteNavData} localizedSiteNavData
 */
export const getLocalizedNavData = (
  siteNavData: SiteNavQueryData,
  shopAllCategory: NavQueryCategory,
  language: string,
  currentMsMarket: string,
  isBlog: boolean,
  navigationStaticText: NavigationStaticText,
  strapiBlogAsset?: StrapiBlogAsset,
): SiteNavData => {
  invariant(siteNavData, "Navigation Data is Required");

  // The following fields will be transformed / localized and returned
  const { categories, localizations, dropdownLinks } = siteNavData;

  const localizedNavigationInformation = getStrapiLocaleContent(language, localizations);

  // Get Localized Product - Returns the localized product. Returns null if product is inactive or does not have a slug.
  const getLocalizedProduct = (product: NavQueryProduct): NavProduct | null => {
    const productLocalizations = getStrapiLocaleContent(language, product.localizations);

    const productBadge = getProductBadgeVariant(product.productBadges, currentMsMarket);

    const localizedProduct = {
      ...product,
      title: productLocalizations?.title ?? product?.title,
      subtitle: productLocalizations?.subtitle ?? product?.subtitle,
      // Page slug links to the product's PDP - if blog, include the baseUrl
      pageSlug: isBlog ? `${baseUrl}/products/${product.slug}` : `/products/${product.slug}`,
      productBadge: productBadge,
    };

    return localizedProduct.active ? localizedProduct : null;
  };

  // Get Localized Navigation Column Content - also accounts for edge cases with inactive products or those without slugs
  const getLocalizedNavColumn = (
    column: NavQueryCategoryColumn | null,
  ): NavCategoryColumn | null => {
    if (column) {
      const localizedNavColumn = {
        id: column.id,
        items: column.items
          ?.map((item) => {
            const localizedItem = getStrapiLocaleContent(language, item.localizations);
            const localizedTitle = localizedItem?.title ?? item.title;
            const shopAllCTAText = `${navigationStaticText.shopAll} ${localizedTitle}`;
            // Get Product List - if getLocalizedProduct returns null
            // (if the product is inactive or does not have a slug), we don't add that product.
            const products = item.products.reduce(
              (localizedProducts: NavProduct[], product: NavQueryProduct) => {
                const localizedProduct = getLocalizedProduct(product);
                if (localizedProduct) {
                  return [...localizedProducts, localizedProduct];
                } else {
                  return localizedProducts;
                }
              },
              [],
            );

            return {
              ...item,
              title: localizedTitle,
              subtitle: localizedItem?.subtitle ?? item.subtitle,
              shopAllCTAText: shopAllCTAText,
              products: products,
            };
          })
          // Account for edge case, where products array has zero length (when it has only inactive products or products without slugs).
          // Also ensures SubCategory has a slug associated with it - otherwise, the collection page links to null.
          // This ensures that we don't return anything for column items since there are no products to display.
          .filter((item) => item.products.length > 0 && item.slug),
      };

      // Strapi can allow us to have an empty items array, in which case there's no column data - return null in this case so we don't render anything
      return localizedNavColumn.items.length > 0 ? localizedNavColumn : null;
    } else {
      return null;
    }
  };

  // Get Localized Navigation Category Content - also adds additional page slug property,
  // which links to the category's collection page
  const getLocalizedNavCategory = (category: NavQueryCategory): NavCategory | null => {
    const { navColumnOne, navColumnTwo, navColumnThree, navColumnFour, clpHeroImage } = category;
    const localizedCategory = getStrapiLocaleContent(language, category.localizations);
    const localizedTitle = localizedCategory?.title ?? category.title;
    const localizedDescription = localizedCategory?.description ?? category.description;

    const localizedNavColumnOne = getLocalizedNavColumn(navColumnOne);
    const localizedNavColumnTwo = getLocalizedNavColumn(navColumnTwo);
    const localizedNavColumnThree = getLocalizedNavColumn(navColumnThree);
    const localizedNavColumnFour = getLocalizedNavColumn(navColumnFour);

    const backCTAText = navigationStaticText.back;
    const shopNowCTAText = navigationStaticText.shopNow;
    const shopAllCTAText = `${navigationStaticText.shopAll} ${localizedTitle}`;

    const featuredImageModule = (() => {
      // If we are in blog and strapiBlogAsset FeaturedImage exists, use that.
      // Dummy fields retain the same shape as FeaturedImageModule.
      if (isBlog && strapiBlogAsset?.featuredImage) {
        return {
          ...strapiBlogAsset.featuredImage,
          shopNowText: shopNowCTAText,
          sectionName: "BlogFeaturedImage",
          product: null,
          showFeaturedImage: true,
          locale: "en",
          localizations: [],
        };
      }

      // Featured Image Module only queried on gatsby-site - null for blog.
      if (category.featuredImageModule) {
        return {
          ...category.featuredImageModule,
          shopNowText: shopNowCTAText,
        };
      }

      return null;
    })();

    const localeClpHeroImage = localizedCategory?.clpHeroImage ?? clpHeroImage;

    // Localized Navigation Category Content
    const localizedNavCategory = {
      ...category,
      isStaticLink: false,
      // Page slug links to the category's collection page - if blog, include the baseUrl
      pageSlug: isBlog
        ? `${baseUrl}/collections/${category.slug}`
        : `/collections/${category.slug}`,
      backCTAText: backCTAText,
      shopAllCTAText: shopAllCTAText,
      featuredImageModule: featuredImageModule,
      title: localizedTitle,
      description: localizedDescription,
      subtitle: localizedCategory?.subtitle ?? category.subtitle,
      eyebrow: localizedCategory?.eyebrow ?? category.eyebrow,
      navColumns: [
        ...(localizedNavColumnOne ? [localizedNavColumnOne] : []),
        ...(localizedNavColumnTwo ? [localizedNavColumnTwo] : []),
        ...(localizedNavColumnThree ? [localizedNavColumnThree] : []),
        ...(localizedNavColumnFour ? [localizedNavColumnFour] : []),
      ],
      clpHeroImage: localeClpHeroImage,
    };

    // Don't return any category that does not have a slug - else it will link to nothing
    return localizedNavCategory.slug && localizedNavCategory.navColumns.length > 0
      ? localizedNavCategory
      : null;
  };

  // Remove any category that may be null (has no sub categories or slug)
  const navigationCategories = categories.reduce(
    (acc: NavCategory[], category: NavQueryCategory | null) => {
      const localizedCategory = category && getLocalizedNavCategory(category);
      if (localizedCategory && localizedCategory.slug !== shopAllCategory.slug) {
        return [...acc, localizedCategory];
      } else {
        return acc;
      }
    },
    [],
  );

  invariant(navigationCategories.length > 0, "Navigation categories are required.");

  // Shop All Category - This is a special category that links to the shop all page - it has to be separate from the other categories
  // in order to be able to filter out categories that don't have any active products
  const localizedShopAllCategory = getStrapiLocaleContent(language, shopAllCategory.localizations);

  const shopAllStaticCategory: NavCategory = {
    ...shopAllCategory,
    isStaticLink: true,
    pageSlug: isBlog
      ? `${baseUrl}/collections/${shopAllCategory.slug}`
      : `/collections/${shopAllCategory.slug}`,
    backCTAText: navigationStaticText.back,
    shopAllCTAText: navigationStaticText.shopAll,
    featuredImageModule: null,
    title: localizedShopAllCategory?.title ?? shopAllCategory.title,
    subtitle: localizedShopAllCategory?.subtitle ?? shopAllCategory.subtitle,
    eyebrow: localizedShopAllCategory?.eyebrow ?? shopAllCategory.eyebrow,
    navColumns: [],
  };

  // Return full localized navigation object
  return {
    ...siteNavData,
    categories: [...navigationCategories, shopAllStaticCategory],
    dropdownLinks: localizedNavigationInformation?.dropdownLinks ?? dropdownLinks,
    // Request to have a different order for categories on mobile
    mobileCategories: [shopAllStaticCategory, ...navigationCategories],
    // Return localized super banner content
  };
};

/**
 * Get Navigation SubCategories
 *
 * This function return an array of all the navigation sub categories that exist in a column.
 * Useful for when we are getting the full list of a columns sub categories, like in the collection pages.
 *
 * @param {NavCategory} category
 * @returns {NavSubCategory[]} subCategories
 */
export const getNavigationSubCategories = (category: NavCategory): NavSubCategory[] =>
  category.navColumns.reduce(
    (subCategories: NavSubCategory[], navCategoryColumn: NavCategoryColumn | null) => {
      const subCategoryItems = navCategoryColumn?.items;
      if (subCategoryItems) {
        return [...subCategories, ...subCategoryItems];
      } else {
        return subCategories;
      }
    },
    [],
  );

/**
 * Get Strapi Size Chart Measurements
 *
 * This function can be used for any module pulling size chart data from Strapi products.
 *
 * The function takes the strapi measurements and creates a structured object of the sizes.
 *
 *
 * @param {BoxerStrapiMeasurementType | TshirtStrapiMeasurementType} measurements
 * @returns {object}
 */
export const getStrapiSizeChartMeasurements = (
  measurements: BoxerStrapiMeasurementType | TshirtStrapiMeasurementType,
) =>
  (
    Object.keys(measurements) as Array<
      keyof BoxerStrapiMeasurementType & keyof TshirtStrapiMeasurementType
    >
  ).reduce((prevMeasurement, currMeasurement) => {
    const filteredMeasurement = Object.keys(measurements[currMeasurement]).reduce((prev, curr) => {
      if (curr.includes(MEASUREMENT_UNIT)) {
        const measurementWithoutPrefix = curr.replace(MEASUREMENT_UNIT, "").toUpperCase();

        return {
          ...prev,
          [measurementWithoutPrefix]: measurements[currMeasurement][curr],
        };
      }

      return prev;
    }, {});

    return { ...prevMeasurement, [currMeasurement]: filteredMeasurement };
  }, {});

/**
 *
 * Get Video Locale Content
 *
 * Helper function to get localized content from video relations
 *
 * @param {string} language
 * @param {StrapiVideoInterface} strapiVideo
 * @returns {string | undefined}
 */
export const getVideoTranscriptLocaleContent = (
  language: string,
  strapiVideo: StrapiVideoInterface | null | undefined,
) => {
  const videoLocaleContent =
    strapiVideo && getStrapiLocaleContent(language, strapiVideo.localizations);

  const localeVideoTranscript =
    videoLocaleContent?.transcript ?? strapiVideo?.transcript?.data?.transcript;

  return localeVideoTranscript;
};

export const pluralize = (number: number | string, singular: string, plural: string): string => {
  if (typeof number === "string") {
    number = parseInt(number);
  }

  return number === 1 ? singular : plural;
};
